import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  AlertCircle,
  Loader2,
  Plus,
  Trash2,
  CalendarClock,
} from "lucide-react";
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Input,
  Label,
  Switch,
} from "@sportscanner/ui";
import {
  coachAvailabilitySchema,
  coachTimeOffSchema,
} from "@sportscanner/schemas";
import { apiGet, apiPut, getApiErrorCode } from "@/lib/api";

interface CoachMe {
  id: string;
  bio: string | null;
  isActive: boolean;
  sports: { sportId: string; sportName: string; pricePerSession: string }[];
  availability: { dayOfWeek: number; startTime: string; endTime: string }[];
  timeOff: {
    id: string;
    date: string;
    startTime: string | null;
    endTime: string | null;
    reason: string | null;
  }[];
}

interface CoachSession {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  playerName: string;
  playerPhone: string | null;
  courtName: string;
  sportName: string;
}

interface Window {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
}
interface TimeOffRow {
  date: string;
  wholeDay: boolean;
  startTime: string;
  endTime: string;
  reason: string;
}

const DOW = [0, 1, 2, 3, 4, 5, 6];

export default function CoachSchedulePage() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const meQuery = useQuery({
    queryKey: ["coaching", "me"],
    queryFn: () => apiGet<CoachMe>("/api/admin/coaching/me"),
  });
  const sessionsQuery = useQuery({
    queryKey: ["coaching", "sessions"],
    queryFn: () =>
      apiGet<{ sessions: CoachSession[] }>("/api/admin/coaching/sessions"),
  });

  const sportLabel = (name: string) =>
    t(`sports.${name}`, { defaultValue: name });

  // Locale-aware weekday name. 2024-01-07 is a Sunday → + dow lands on the day.
  const weekdayLabel = (dow: number) =>
    new Intl.DateTimeFormat(i18n.language, { weekday: "long" }).format(
      new Date(Date.UTC(2024, 0, 7 + dow)),
    );

  if (meQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
      </div>
    );
  }
  if (meQuery.isError || !meQuery.data) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-3xl mx-auto">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{t("errors.api.INTERNAL_ERROR")}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const me = meQuery.data;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8 max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="font-heading text-xl sm:text-2xl font-bold text-neutral-800 mb-1">
          {t("coaching.title")}
        </h1>
        <p className="text-sm text-neutral-500">{t("coaching.subtitle")}</p>
      </div>

      {/* Sports the manager assigned + bookable status */}
      <div className="bg-white rounded-xl border border-neutral-200 p-4 sm:p-6 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-semibold text-neutral-700">
            {t("coaching.yourSports")}
          </h2>
          <Badge variant={me.isActive ? "default" : "secondary"}>
            {me.isActive
              ? t("coaching.statusActive")
              : t("coaching.statusInactive")}
          </Badge>
        </div>
        {me.sports.length === 0 ? (
          <p className="text-sm text-neutral-500">{t("coaching.noSports")}</p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {me.sports.map((s) => (
              <li
                key={s.sportId}
                className="text-sm rounded-lg border border-neutral-200 px-3 py-1.5 text-neutral-700"
              >
                {sportLabel(s.sportName)} · {s.pricePerSession}
              </li>
            ))}
          </ul>
        )}
      </div>

      <AvailabilityEditor
        initial={me.availability}
        weekdayLabel={weekdayLabel}
        onSaved={() =>
          queryClient.invalidateQueries({ queryKey: ["coaching"] })
        }
      />

      <TimeOffEditor
        initial={me.timeOff}
        onSaved={() =>
          queryClient.invalidateQueries({ queryKey: ["coaching"] })
        }
      />

      {/* Upcoming sessions */}
      <div className="bg-white rounded-xl border border-neutral-200 p-4 sm:p-6 space-y-3">
        <h2 className="font-semibold text-neutral-700 flex items-center gap-2">
          <CalendarClock className="w-4 h-4 text-neutral-400" />
          {t("coaching.upcomingSessions")}
        </h2>
        {sessionsQuery.data && sessionsQuery.data.sessions.length > 0 ? (
          <ul className="divide-y divide-neutral-100">
            {sessionsQuery.data.sessions.map((s) => (
              <li
                key={s.id}
                className="py-2.5 flex items-center justify-between gap-3 text-sm"
              >
                <div className="min-w-0">
                  <p className="font-medium text-neutral-800 truncate">
                    {s.playerName}
                  </p>
                  <p className="text-neutral-500">
                    {sportLabel(s.sportName)} · {s.courtName}
                  </p>
                </div>
                <div className="text-right shrink-0 text-neutral-600">
                  <p>{s.date}</p>
                  <p>
                    {s.startTime}–{s.endTime}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-neutral-500">{t("coaching.noSessions")}</p>
        )}
      </div>
    </div>
  );
}

function AvailabilityEditor({
  initial,
  weekdayLabel,
  onSaved,
}: {
  initial: Window[];
  weekdayLabel: (dow: number) => string;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [windows, setWindows] = useState<Window[]>(initial);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  useEffect(() => {
    setWindows(initial);
  }, [initial]);

  const byDay = useMemo(() => {
    const m = new Map<number, { index: number; w: Window }[]>();
    windows.forEach((w, index) => {
      const arr = m.get(w.dayOfWeek) ?? [];
      arr.push({ index, w });
      m.set(w.dayOfWeek, arr);
    });
    return m;
  }, [windows]);

  const update = (index: number, patch: Partial<Window>) =>
    setWindows((prev) =>
      prev.map((w, i) => (i === index ? { ...w, ...patch } : w)),
    );
  const remove = (index: number) =>
    setWindows((prev) => prev.filter((_, i) => i !== index));
  const add = (dayOfWeek: number) =>
    setWindows((prev) => [
      ...prev,
      { dayOfWeek, startTime: "09:00", endTime: "10:00" },
    ]);

  const mutation = useMutation({
    mutationFn: async () => {
      const parsed = coachAvailabilitySchema.safeParse({ windows });
      if (!parsed.success) {
        throw {
          error: parsed.error.issues[0]?.message ?? "VALIDATION_FAILED",
        };
      }
      await apiPut("/api/admin/coaching/availability", parsed.data);
    },
    onSuccess: () => {
      setErrorCode(null);
      toast.success(t("common.saved"));
      onSaved();
    },
    onError: (err) => setErrorCode(getApiErrorCode(err)),
  });

  return (
    <div className="bg-white rounded-xl border border-neutral-200 p-4 sm:p-6 space-y-4">
      <div>
        <h2 className="font-semibold text-neutral-700">
          {t("coaching.availabilityTitle")}
        </h2>
        <p className="text-sm text-neutral-500 mt-0.5">
          {t("coaching.availabilityHint")}
        </p>
      </div>

      {errorCode && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{t(`errors.api.${errorCode}`)}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-3">
        {DOW.map((dow) => (
          <div
            key={dow}
            className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4 border-b border-neutral-100 pb-3 last:border-0"
          >
            <div className="w-28 shrink-0 text-sm font-medium text-neutral-700 pt-1.5 capitalize">
              {weekdayLabel(dow)}
            </div>
            <div className="flex-1 space-y-2">
              {(byDay.get(dow) ?? []).map(({ index, w }) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    type="time"
                    value={w.startTime}
                    onChange={(e) =>
                      update(index, { startTime: e.target.value })
                    }
                    className="w-32 rounded-lg border-neutral-200"
                  />
                  <span className="text-neutral-400">–</span>
                  <Input
                    type="time"
                    value={w.endTime}
                    onChange={(e) => update(index, { endTime: e.target.value })}
                    className="w-32 rounded-lg border-neutral-200"
                  />
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    className="text-neutral-400 hover:text-red-500 cursor-pointer p-1"
                    aria-label={t("common.remove")}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => add(dow)}
                className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 cursor-pointer"
              >
                <Plus className="w-4 h-4" />
                {t("coaching.addWindow")}
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end">
        <Button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          className="bg-primary-600 hover:bg-primary-700 text-white"
        >
          {mutation.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            t("common.save")
          )}
        </Button>
      </div>
    </div>
  );
}

function TimeOffEditor({
  initial,
  onSaved,
}: {
  initial: CoachMe["timeOff"];
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<TimeOffRow[]>(
    initial.map((e) => ({
      date: e.date,
      wholeDay: e.startTime == null,
      startTime: e.startTime ?? "09:00",
      endTime: e.endTime ?? "10:00",
      reason: e.reason ?? "",
    })),
  );
  const [errorCode, setErrorCode] = useState<string | null>(null);

  useEffect(() => {
    setRows(
      initial.map((e) => ({
        date: e.date,
        wholeDay: e.startTime == null,
        startTime: e.startTime ?? "09:00",
        endTime: e.endTime ?? "10:00",
        reason: e.reason ?? "",
      })),
    );
  }, [initial]);

  const today = new Date().toISOString().slice(0, 10);
  const update = (index: number, patch: Partial<TimeOffRow>) =>
    setRows((prev) =>
      prev.map((r, i) => (i === index ? { ...r, ...patch } : r)),
    );
  const remove = (index: number) =>
    setRows((prev) => prev.filter((_, i) => i !== index));
  const add = () =>
    setRows((prev) => [
      ...prev,
      {
        date: today,
        wholeDay: true,
        startTime: "09:00",
        endTime: "10:00",
        reason: "",
      },
    ]);

  const mutation = useMutation({
    mutationFn: async () => {
      const entries = rows.map((r) => ({
        date: r.date,
        startTime: r.wholeDay ? null : r.startTime,
        endTime: r.wholeDay ? null : r.endTime,
        reason: r.reason.trim() || null,
      }));
      const parsed = coachTimeOffSchema.safeParse({ entries });
      if (!parsed.success) {
        throw {
          error: parsed.error.issues[0]?.message ?? "VALIDATION_FAILED",
        };
      }
      await apiPut("/api/admin/coaching/time-off", parsed.data);
    },
    onSuccess: () => {
      setErrorCode(null);
      toast.success(t("common.saved"));
      onSaved();
    },
    onError: (err) => setErrorCode(getApiErrorCode(err)),
  });

  return (
    <div className="bg-white rounded-xl border border-neutral-200 p-4 sm:p-6 space-y-4">
      <div>
        <h2 className="font-semibold text-neutral-700">
          {t("coaching.timeOffTitle")}
        </h2>
        <p className="text-sm text-neutral-500 mt-0.5">
          {t("coaching.timeOffHint")}
        </p>
      </div>

      {errorCode && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{t(`errors.api.${errorCode}`)}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-3">
        {rows.length === 0 && (
          <p className="text-sm text-neutral-500">{t("coaching.noTimeOff")}</p>
        )}
        {rows.map((r, index) => (
          <div
            key={index}
            className="flex flex-wrap items-center gap-2 border-b border-neutral-100 pb-3 last:border-0"
          >
            <Input
              type="date"
              value={r.date}
              onChange={(e) => update(index, { date: e.target.value })}
              className="w-40 rounded-lg border-neutral-200"
            />
            <label className="flex items-center gap-2 text-sm text-neutral-600">
              <Switch
                checked={r.wholeDay}
                onCheckedChange={(v) => update(index, { wholeDay: v })}
              />
              {t("coaching.wholeDay")}
            </label>
            {!r.wholeDay && (
              <div className="flex items-center gap-2">
                <Input
                  type="time"
                  value={r.startTime}
                  onChange={(e) => update(index, { startTime: e.target.value })}
                  className="w-28 rounded-lg border-neutral-200"
                />
                <span className="text-neutral-400">–</span>
                <Input
                  type="time"
                  value={r.endTime}
                  onChange={(e) => update(index, { endTime: e.target.value })}
                  className="w-28 rounded-lg border-neutral-200"
                />
              </div>
            )}
            <Input
              type="text"
              placeholder={t("coaching.reasonPlaceholder")}
              value={r.reason}
              onChange={(e) => update(index, { reason: e.target.value })}
              className="flex-1 min-w-[140px] rounded-lg border-neutral-200"
            />
            <button
              type="button"
              onClick={() => remove(index)}
              className="text-neutral-400 hover:text-red-500 cursor-pointer p-1"
              aria-label={t("common.remove")}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={add}
          className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 cursor-pointer"
        >
          <Plus className="w-4 h-4" />
          {t("coaching.addTimeOff")}
        </button>
      </div>

      <div className="flex justify-end">
        <Button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          className="bg-primary-600 hover:bg-primary-700 text-white"
        >
          {mutation.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            t("common.save")
          )}
        </Button>
      </div>
    </div>
  );
}
